// src/api/paymentAPI.js
import apiClient from './client';

const STRIPE_URL = '/api/v1/stripe';
const SESSION_PAYMENTS_URL = '/api/v1/session_payments';
const PAYMENT_TRANSACTIONS_URL = '/api/v1/payment_transactions';

// Get session payment status
export const getSessionPaymentStatus = async (sessionId) => {
  try {
    const response = await apiClient.get(`${SESSION_PAYMENTS_URL}/${sessionId}/status`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching payment status for session ${sessionId}:`, error);
    throw error;
  }
};

// Enhanced createPaymentIntent with customer support
export const createPaymentIntent = async (amount, currency = 'nzd', description = null, sessionId = null, customerId = null) => {
  try {
    let companyId = null;
    
    // If sessionId is provided, get company_id from existing payment transaction
    if (sessionId) {
      try {
        console.log(`Fetching payment transaction for session ${sessionId}...`);
        
        // Call the payment transactions endpoint to get company_id
        const transactionResponse = await apiClient.get(`${PAYMENT_TRANSACTIONS_URL}`, {
          params: { session_id: sessionId }
        });
        
        if (transactionResponse.data && transactionResponse.data.length > 0) {
          const paymentTransaction = transactionResponse.data[0];
          companyId = paymentTransaction.PaymentTransactionCompanyId;
          console.log(`Found company_id: ${companyId} for session ${sessionId}`);
        } else {
          console.warn(`No payment transaction found for session ${sessionId}`);
        }
      } catch (error) {
        console.warn('Could not fetch payment transaction:', error.message);
      }
    }

    // If we still don't have company_id, throw an error
    if (!companyId) {
      throw new Error('Company ID is required but could not be determined from session');
    }

    const requestData = {
      amount,
      currency,
      description,
      company_id: companyId,
      ...(sessionId && { session_id: sessionId }),
      ...(customerId && { customer_id: customerId })
    };

    console.log('Creating payment intent with data:', requestData);

    const response = await apiClient.post(`${STRIPE_URL}/create-payment-intent`, requestData);
    return response.data;
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw error;
  }
};

// Create Stripe customer
export const createStripeCustomer = async (email, name) => {
  try {
    console.log('Creating Stripe customer:', { email, name });
    const response = await apiClient.post(`${STRIPE_URL}/create-customer`, {
      email,
      name
    });
    return response.data;
  } catch (error) {
    console.error('Error creating Stripe customer:', error);
    // For now, return a mock response if backend doesn't support it yet
    return {
      customer: {
        id: `mock_cus_${Date.now()}`,
        email,
        name
      }
    };
  }
};

// Get customer payment methods
export const getCustomerPaymentMethods = async (customerId) => {
  try {
    const response = await apiClient.get(`${STRIPE_URL}/customer/${customerId}/payment-methods`);
    return response.data;
  } catch (error) {
    console.error('Error fetching customer payment methods:', error);
    throw error;
  }
};

// Alternative approach: Create a helper function to get company_id first
export const getCompanyIdFromSession = async (sessionId) => {
  try {
    const response = await apiClient.get(`${PAYMENT_TRANSACTIONS_URL}`, {
      params: { session_id: sessionId }
    });
    
    if (response.data && response.data.length > 0) {
      return response.data[0].PaymentTransactionCompanyId;
    }
    
    throw new Error(`No payment transaction found for session ${sessionId}`);
  } catch (error) {
    console.error(`Error fetching company ID for session ${sessionId}:`, error);
    throw error;
  }
};

// Get payment intent details
export const getPaymentIntent = async (paymentIntentId) => {
  try {
    const response = await apiClient.get(`${STRIPE_URL}/payment-intent/${paymentIntentId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching payment intent ${paymentIntentId}:`, error);
    throw error;
  }
};

// Update payment transaction status (for manual updates if needed)
export const updatePaymentTransactionStatus = async (transactionId, paymentStatus) => {
  try {
    const response = await apiClient.put(
      `${SESSION_PAYMENTS_URL}/transaction/${transactionId}/status`,
      null,
      { params: { payment_status: paymentStatus } }
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating payment transaction ${transactionId}:`, error);
    throw error;
  }
};

// Mark session as paid
export const markSessionAsPaid = async (sessionId, paymentTransactionId) => {
  try {
    const response = await apiClient.post(
      `${SESSION_PAYMENTS_URL}/${sessionId}/mark_paid`,
      null,
      { params: { payment_transaction_id: paymentTransactionId } }
    );
    return response.data;
  } catch (error) {
    console.error(`Error marking session ${sessionId} as paid:`, error);
    throw error;
  }
};

// Customer management functions
export const createCustomer = async (firstName = null, lastName = null) => {
  try {
    const response = await apiClient.post(`${STRIPE_URL}/customers`, {
      first_name: firstName,
      last_name: lastName
    });
    return response.data;
  } catch (error) {
    console.error('Error creating customer:', error);
    throw error;
  }
};

export const getMyCustomer = async () => {
  try {
    const response = await apiClient.get(`${STRIPE_URL}/customers/me`);
    return response.data;
  } catch (error) {
    console.error('Error getting customer:', error);
    throw error;
  }
};

// Payment method management functions
export const savePaymentMethod = async (paymentMethodId, setAsDefault = false) => {
  try {
    const response = await apiClient.post(`${STRIPE_URL}/payment-methods`, {
      payment_method_id: paymentMethodId,
      set_as_default: setAsDefault
    });
    return response.data;
  } catch (error) {
    console.error('Error saving payment method:', error);
    throw error;
  }
};

export const getSavedPaymentMethods = async () => {
  try {
    const response = await apiClient.get(`${STRIPE_URL}/payment-methods`);
    return response.data;
  } catch (error) {
    console.error('Error getting saved payment methods:', error);
    throw error;
  }
};

export const deletePaymentMethod = async (paymentMethodId) => {
  try {
    const response = await apiClient.delete(`${STRIPE_URL}/payment-methods/${paymentMethodId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting payment method:', error);
    throw error;
  }
};

export const setDefaultPaymentMethod = async (paymentMethodId) => {
  try {
    const response = await apiClient.put(`${STRIPE_URL}/payment-methods/${paymentMethodId}/default`);
    return response.data;
  } catch (error) {
    console.error('Error setting default payment method:', error);
    throw error;
  }
};

// Enhanced payment intent creation with customer support
export const createPaymentIntentWithCustomer = async (
  amount, 
  currency = 'nzd', 
  description = null, 
  sessionId = null, 
  paymentMethodId = null,
  savePaymentMethod = false
) => {
  try {
    const requestData = {
      amount,
      currency,
      description,
      session_id: sessionId,
      payment_method_id: paymentMethodId,
      save_payment_method: savePaymentMethod
    };

    console.log('Creating payment intent with customer:', requestData);

    const response = await apiClient.post(`${STRIPE_URL}/create-payment-intent-with-customer`, requestData);
    return response.data;
  } catch (error) {
    console.error('Error creating payment intent with customer:', error);
    throw error;
  }
};

// Save payment method from completed payment intent
export const savePaymentMethodFromIntent = async (paymentIntentId, setAsDefault = false) => {
  try {
    const response = await apiClient.post(`${STRIPE_URL}/save-payment-method-from-intent`, null, {
      params: {
        payment_intent_id: paymentIntentId,
        set_as_default: setAsDefault
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error saving payment method from intent:', error);
    throw error;
  }
};